/**
 * Vue 3 Composable for API calls
 * Centralizes API logic, error handling, and authentication
 */

import { ref, computed } from 'vue';
import { buildApiUrl, API_ENDPOINTS } from '@/config/api';

export function useApi() {
  // Reactive state
  const isLoading = ref(false);
  const error = ref(null);

  // Generic API call wrapper (cookie-based auth)
  const apiCall = async (endpoint, options = {}) => {
    isLoading.value = true;
    error.value = null;

    try {
      const url = buildApiUrl(endpoint);
      const config = {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {})
        },
        ...options
      };

      const response = await fetch(url, config);

      if (response.status === 401) {
        const err = new Error('401 Unauthorized');
        err.isSessionExpired = true;
        throw err;
      }

      if (!response.ok) {
        const errorText = await response.text();
        let cleanErrorMessage = errorText;
        if (errorText.startsWith('"') && errorText.endsWith('"')) {
          cleanErrorMessage = errorText.slice(1, -1);
        }
        throw new Error(cleanErrorMessage || `HTTP ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return await response.text();
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Account API methods
  const accountApi = {
    async signup(name, email) {
      return apiCall(API_ENDPOINTS.REQUEST_SIGNUP_CODE, {
        method: 'POST',
        body: JSON.stringify({ name, email })
      });
    },
    async createAccount(name, email, code) {
      return apiCall(API_ENDPOINTS.CREATE_ACCOUNT, {
        method: 'POST',
        body: JSON.stringify({ name, email, code })
      });
    },
    async requestLoginCode(email) {
      return apiCall(API_ENDPOINTS.REQUEST_LOGIN_CODE, {
        method: 'POST',
        body: JSON.stringify({ email })
      });
    },
    async login(email, code) {
      return apiCall(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        body: JSON.stringify({ email, code })
      });
    },
    async logout() {
      await apiCall(API_ENDPOINTS.LOGOUT, { method: 'POST' });
    },
    async getAccount() {
      return apiCall(API_ENDPOINTS.GET_ACCOUNT, { method: 'GET' });
    },
    async requestAccountUpdateCode(email) {
      return apiCall(API_ENDPOINTS.REQUEST_ACCOUNT_UPDATE_CODE, {
        method: 'POST',
        body: JSON.stringify({ email })
      });
    },
    async verifyAccountUpdateCode(email, code) {
      return apiCall(API_ENDPOINTS.VERIFY_ACCOUNT_UPDATE_CODE, {
        method: 'POST',
        body: JSON.stringify({ email, verificationCode: code })
      });
    },
    async updateAccount(name, email, verificationCode = '', msgFrequency, phoneNumber = '') {
      const updateData = { name, email, msgFrequency };
      if (verificationCode) {
        updateData.verificationCode = verificationCode;
      }
      if (phoneNumber) {
        updateData.phoneNumber = phoneNumber;
      }
      return apiCall(API_ENDPOINTS.UPDATE_ACCOUNT, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
    },
    async deleteAccount() {
      return apiCall(API_ENDPOINTS.DELETE_ACCOUNT, { method: 'DELETE' });
    },
    async createCheckoutSession(phoneNumber) {
      return apiCall(API_ENDPOINTS.CREATE_CHECKOUT_SESSION, {
        method: 'POST',
        body: JSON.stringify({ phoneNumber })
      });
    },
    async enableIncludedSms(phoneNumber) {
      return apiCall(API_ENDPOINTS.ENABLE_INCLUDED_SMS, {
        method: 'POST',
        body: JSON.stringify({ phoneNumber })
      });
    },
    async disableIncludedSms() {
      return apiCall(API_ENDPOINTS.DISABLE_INCLUDED_SMS, {
        method: 'POST'
      });
    },
    async createCustomerPortalSession() {
      return apiCall(API_ENDPOINTS.CREATE_CUSTOMER_PORTAL_SESSION, {
        method: 'POST'
      });
    },
    async unsubscribe(token) {
      return apiCall(`${API_ENDPOINTS.UNSUBSCRIBE}?token=${encodeURIComponent(token)}`, { 
        method: 'GET' 
      });
    }
  };

  // Moments API methods
  const momentsApi = {
    async create(momentText) {
      return apiCall(API_ENDPOINTS.CREATE_MOMENT, {
        method: 'POST',
        body: JSON.stringify({ momentDetails: momentText })
      });
    },
    async getMoments() {
      return apiCall(API_ENDPOINTS.GET_MOMENTS, { method: 'GET' });
    },
    async update(momentId, momentDetails) {
      return apiCall(API_ENDPOINTS.UPDATE_MOMENT, {
        method: 'PUT',
        body: JSON.stringify({ momentId, momentDetails })
      });
    },
    async deleteMoment(momentId) {
      return apiCall(API_ENDPOINTS.DELETE_MOMENT, {
        method: 'DELETE',
        body: JSON.stringify({ momentId })
      });
    }
  };

  // Contact API methods
  const contactApi = {
    async submitForm(email, type, message) {
      return apiCall(API_ENDPOINTS.SUBMIT_CONTACT_FORM, {
        method: 'POST',
        body: JSON.stringify({ email, type, message })
      });
    }
  };

  // Return the composable interface
  return {
    // State
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    // Methods
    clearError: () => { error.value = null; },
    // API groups
    account: accountApi,
    moments: momentsApi,
    contact: contactApi,
    // Low-level access for custom calls
    apiCall
  };
}
